import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowClockwise, Path, Info, CheckCircle } from "@phosphor-icons/react";
import { Empty, ResetConfirm } from "./IQTestCoachPanel";

/**
 * Job-coach view of a coachee's Loopbaanwaardentest.
 * 6 screens; shows X / 6 completed, then each screen's ranking when done.
 */
export default function CareerValuesCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/career-values`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.post(`/coachees/${coacheeId}/career-values/reset`);
      setConfirmOpen(false);
      await fetchSession();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResetting(false); }
  };

  const screens = session?.screens || [];
  const rankings = session?.rankings || {};
  const completedScreens = session?.completed_screens || [];
  const totalScreens = session?.screen_count || screens.length;
  const completed = session?.completed === true;
  const state = completed ? "completed" : completedScreens.length > 0 ? "in_progress" : "not_started";
  const labelByKey = (screen, key) =>
    (screen.values || []).find((v) => v.key === key)?.label || key;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="career-values-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <Path size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Loopbaanwaardentest</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} rangschikt loopbaanwaarden over {totalScreens} schermen.
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500" data-testid="career-values-coach-counter">
                Status: <strong className="text-zinc-900">{cvStateLabel(state)}</strong>
                {state !== "not_started" && (
                  <span> — {completedScreens.length} / {totalScreens} schermen klaar</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={resetting || state === "not_started"}
                data-testid="career-values-coach-reset-btn"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <ArrowClockwise size={16} /> Test resetten
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="career-values-coach-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : state === "not_started" ? (
        <Empty coacheeName={coacheeName} testid="career-values-coach-empty" text="heeft de loopbaanwaardentest nog niet gestart" />
      ) : (
        <div className="space-y-3" data-testid="career-values-coach-screens">
          {screens.map((screen) => {
            const ranking = rankings[screen.id];
            const done = Array.isArray(ranking) && ranking.length === (screen.values || []).length;
            return (
              <div key={screen.id} className="neuro-card p-5 bg-white" data-testid={`career-values-coach-screen-${screen.id}`}>
                <div className="flex items-start gap-3">
                  {done ? (
                    <CheckCircle size={22} className="text-[#43A047] shrink-0 mt-0.5" />
                  ) : (
                    <Info size={22} className="text-zinc-400 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-display text-base font-bold tracking-tight">{screen.title}</h4>
                    {done ? (
                      <ol className="mt-2 space-y-1 list-decimal list-inside">
                        {ranking.map((key, idx) => (
                          <li key={key} className="text-sm">
                            <span className={"font-medium " + (idx < 2 ? "text-[#1E88E5]" : "text-zinc-800")}>
                              {labelByKey(screen, key)}
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-sm text-zinc-500 mt-1">Nog niet beantwoord.</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {completed && session?.completed_at && (
            <div className="text-xs text-zinc-400 px-1">
              Afgerond op {new Date(session.completed_at).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {confirmOpen && (
        <ResetConfirm
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleReset}
          busy={resetting}
          coacheeName={coacheeName}
          testName="loopbaanwaardentest"
          testid="career-values-coach"
        />
      )}
    </div>
  );
}

function cvStateLabel(state) {
  if (state === "completed") return "Afgerond";
  if (state === "in_progress") return "Bezig";
  return "Nog niet gestart";
}
